import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
  </svg>
);

const tocItems = [
  { id: 'pp-01', label: 'What Data We Collect' },
  { id: 'pp-02', label: 'How We Use Your Data' },
  { id: 'pp-03', label: 'Data Security' },
  { id: 'pp-04', label: 'Third-Party Sharing' },
  { id: 'pp-05', label: 'Cookies & Tracking' },
  { id: 'pp-06', label: 'Your Rights' },
  { id: 'pp-07', label: 'Data Retention' },
  { id: 'pp-08', label: 'Policy Updates' },
];

const PrivacyPolicy = () => {
  const [activeId, setActiveId] = useState('pp-01');
  const clickedRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (clickedRef.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-140px 0px -50% 0px', threshold: 0 }
    );
    tocItems.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    setActiveId(id);
    clickedRef.current = true;
    const el = document.getElementById(id);
    if (el) {
      const navbar = document.querySelector('.navbar-modern');
      const navbarHeight = navbar ? navbar.offsetHeight : 130;
      const top = el.getBoundingClientRect().top + window.scrollY - navbarHeight - 24;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    setTimeout(() => { clickedRef.current = false; }, 1800);
  };

  return (
    <div className="legal-page">
      <section className="legal-hero">
        <div className="legal-hero-grid" />
        <div className="container">
          <div className="legal-hero-inner">
            <div className="legal-hero-tag">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              Privacy Policy
            </div>
            <h1 className="legal-hero-title">Your Privacy <span>Matters to Us</span></h1>
            <p className="legal-hero-subtitle">
              We are transparent about how we handle your data. This document explains what we collect, why we collect it, and how we keep it safe.
            </p>
            <div className="legal-hero-pills">
              <span className="legal-pill">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Effective: January 2026
              </span>
              <span className="legal-pill">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                </svg>
                Last Updated: January 2026
              </span>
              <span className="legal-pill">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><path d="M2 12h20"/>
                </svg>
                Applies Globally
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="legal-layout">
        <aside className="legal-toc">
          <div className="legal-toc-header">
            <p className="legal-toc-title">On This Page</p>
          </div>
          <ul className="legal-toc-list">
            {tocItems.map((item, i) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={activeId === item.id ? 'toc-active' : ''}
                  onClick={(e) => { e.preventDefault(); scrollTo(item.id); }}
                >
                  <span className="toc-num">{String(i + 1).padStart(2, '0')}</span>
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </aside>

        <main className="legal-main">
          <div className="legal-intro-card">
            <div className="legal-intro-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <p>
              Elite Freight Forwarders Network ("EFFN", "we", "us", "our") respects your privacy and is committed to handling your personal information responsibly. This Privacy Policy describes the types of information we gather, the purposes for which we use it, and the steps we take to protect it. By using our website or submitting any form, you acknowledge that you have read and understood this policy.
            </p>
          </div>

          <div className={`legal-section${activeId === 'pp-01' ? ' legal-section--active' : ''}`} id="pp-01">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 01</span>
                <h2 className="legal-section-title">What Data We Collect</h2>
              </div>
            </div>
            <p>When you interact with our website — whether by filling out a contact form, submitting a membership application, or simply browsing — we may collect the following categories of information:</p>
            <ul className="legal-list">
              {[
                'Full name and professional title',
                'Business name, registered address, and website URL',
                'Email address and telephone number',
                'Country or region of operation',
                'Nature of your inquiry or message',
                'Company logo or supporting documents (when voluntarily uploaded)',
                'Selected membership plan and related preferences',
              ].map((item, i) => <li key={i}><CheckIcon />{item}</li>)}
            </ul>
            <div className="legal-highlight">
              <InfoIcon />
              <span>We also collect anonymised technical data automatically — such as browser type, device information, referring URLs, and pages visited — through standard web analytics tools. This data cannot be used to identify you personally.</span>
            </div>
          </div>

          <div className={`legal-section${activeId === 'pp-02' ? ' legal-section--active' : ''}`} id="pp-02">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 02</span>
                <h2 className="legal-section-title">How We Use Your Data</h2>
              </div>
            </div>
            <p>We use the information we collect strictly for the following purposes:</p>
            <ul className="legal-list">
              {[
                'Reviewing and processing membership applications',
                'Responding to inquiries and providing requested information',
                'Sending updates about network events, announcements, and relevant industry news (only with your consent)',
                'Improving the functionality and content of our website',
                'Fulfilling legal, regulatory, or contractual obligations',
                'Preventing fraud and maintaining the security of our platform',
              ].map((item, i) => <li key={i}><CheckIcon />{item}</li>)}
            </ul>
            <div className="legal-highlight">
              <InfoIcon />
              <span>We do not sell, rent, lease, or otherwise transfer your personal data to any third party for commercial or marketing purposes. Your information is used solely to serve you and operate our network.</span>
            </div>
          </div>

          <div className={`legal-section${activeId === 'pp-03' ? ' legal-section--active' : ''}`} id="pp-03">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 03</span>
                <h2 className="legal-section-title">Data Security</h2>
              </div>
            </div>
            <p>Protecting your data is a priority. We apply a combination of technical and organisational safeguards to prevent unauthorised access, accidental loss, alteration, or disclosure of your personal information.</p>
            <ul className="legal-list">
              {[
                'All data transmitted between your browser and our servers is encrypted via SSL/TLS',
                'Access to personal data is limited to authorised staff with a legitimate need',
                'We conduct periodic reviews of our data handling and security practices',
                'Sensitive form submissions are processed over secure, encrypted connections',
              ].map((item, i) => <li key={i}><CheckIcon />{item}</li>)}
            </ul>
            <p>While we take every reasonable precaution, no method of transmission over the internet is completely secure. We encourage you to use strong passwords and to contact us immediately if you suspect any unauthorised use of your information.</p>
          </div>

          <div className={`legal-section${activeId === 'pp-04' ? ' legal-section--active' : ''}`} id="pp-04">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 04</span>
                <h2 className="legal-section-title">Third-Party Sharing</h2>
              </div>
            </div>
            <p>We do not share your personal data with outside parties except in the specific situations listed below:</p>
            <ul className="legal-list">
              {[
                'Service providers who support our website operations (e.g. hosting, analytics), bound by confidentiality obligations',
                'When disclosure is required by applicable law, court order, or government authority',
                'To protect the legitimate interests, safety, or rights of EFFN, its members, or the public',
                'With your explicit prior consent for a specific purpose',
              ].map((item, i) => <li key={i}><CheckIcon />{item}</li>)}
            </ul>
            <div className="legal-highlight">
              <InfoIcon />
              <span>Any third-party service provider we engage is contractually required to handle your data in accordance with applicable privacy laws and our own data protection standards.</span>
            </div>
          </div>

          <div className={`legal-section${activeId === 'pp-05' ? ' legal-section--active' : ''}`} id="pp-05">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 05</span>
                <h2 className="legal-section-title">Cookies & Tracking</h2>
              </div>
            </div>
            <p>Our website uses cookies and similar technologies to improve your experience and gather aggregated usage statistics. We use the following tools:</p>
            <ul className="legal-list">
              {[
                'Google Analytics (GA4) — to understand how visitors navigate our site',
                'Meta Pixel — to measure the effectiveness of our outreach',
                'Session cookies — to maintain basic website functionality',
              ].map((item, i) => <li key={i}><CheckIcon />{item}</li>)}
            </ul>
            <p>These tools collect anonymised, aggregated data and are not used to personally identify you. You can manage or disable cookies through your browser settings at any time. Disabling cookies may affect certain features of the website.</p>
          </div>

          <div className={`legal-section${activeId === 'pp-06' ? ' legal-section--active' : ''}`} id="pp-06">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 06</span>
                <h2 className="legal-section-title">Your Rights</h2>
              </div>
            </div>
            <p>Depending on your location, you may be entitled to exercise the following rights in relation to your personal data:</p>
            <ul className="legal-list">
              {[
                'Access — request a copy of the personal data we hold about you',
                'Correction — ask us to update or correct inaccurate information',
                'Deletion — request that we erase your data where no longer necessary',
                'Restriction — ask us to limit how we process your data in certain circumstances',
                'Portability — receive your data in a structured, machine-readable format',
                'Objection — object to processing based on legitimate interests',
                'Withdrawal of consent — withdraw consent at any time where processing is consent-based',
              ].map((item, i) => <li key={i}><CheckIcon />{item}</li>)}
            </ul>
            <div className="legal-highlight">
              <InfoIcon />
              <span>To exercise any of these rights, please write to us at <a href="mailto:info@effnetwork.com" style={{color:'var(--theme-primary,#0f2f8f)', fontWeight: 600}}>info@effnetwork.com</a>. We will respond within 30 days of receiving your request.</span>
            </div>
          </div>

          <div className={`legal-section${activeId === 'pp-07' ? ' legal-section--active' : ''}`} id="pp-07">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 07</span>
                <h2 className="legal-section-title">Data Retention</h2>
              </div>
            </div>
            <p>We keep your personal data only for as long as it is needed to fulfil the purpose for which it was collected, or as required by law. Our general retention periods are:</p>
            <ul className="legal-list">
              {[
                'Active membership records — retained for the full membership term plus 3 years',
                'Contact and inquiry submissions — retained for up to 24 months',
                'Analytics data — retained in anonymised form for up to 26 months',
                'Legal and financial records — retained as required by applicable regulations',
              ].map((item, i) => <li key={i}><CheckIcon />{item}</li>)}
            </ul>
            <p>Once data is no longer required, it is securely deleted or anonymised. You may request earlier deletion by contacting us directly.</p>
          </div>

          <div className={`legal-section${activeId === 'pp-08' ? ' legal-section--active' : ''}`} id="pp-08">
            <div className="legal-section-header">
              <div className="legal-section-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <div className="legal-section-meta">
                <span className="legal-section-num">Section 08</span>
                <h2 className="legal-section-title">Policy Updates</h2>
              </div>
            </div>
            <p>We may revise this Privacy Policy periodically to reflect changes in our practices, technology, or legal requirements. When we make significant changes, we will update the effective date shown at the top of this page.</p>
            <p>We recommend reviewing this policy from time to time. Continued use of our website after any update constitutes your acceptance of the revised policy. If changes materially affect how we handle your data, we will make reasonable efforts to notify active members directly.</p>
          </div>

          <div className="legal-cta-box">
            <div className="legal-cta-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <h3>Questions About Your Privacy?</h3>
            <p>Our team is happy to clarify anything in this policy or assist with any data-related requests you may have.</p>
            <div className="legal-cta-buttons">
              <Link to="/contact" className="btn-legal-primary">
                Contact Us
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
              <Link to="/terms" className="btn-legal-secondary">View Terms of Service</Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
